import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { knownRoles } from './cells';

const NEW_ROLE = '__new__';

/**
 * A role for a visitor's account: the roles the startup already uses in a dropdown, or a new one
 * typed in. A founder did not know which role names existed from a bare text box (testing,
 * 22 Sep 2026).
 */
export function RolePicker({
  id,
  value,
  onChange,
  usedRoles,
  label,
  className,
  testId,
}: {
  id?: string;
  value: string;
  onChange: (role: string) => void;
  /** Roles accounts of the startup have now. */
  usedRoles: string[];
  label?: string;
  className?: string;
  testId?: string;
}): React.ReactElement {
  const { t } = useTranslation();
  const roles = knownRoles(usedRoles, value);
  const [typing, setTyping] = useState(false);

  if (typing) {
    return (
      <input
        id={id}
        data-testid={testId}
        aria-label={label}
        autoFocus
        value={value}
        placeholder={t('startupData.roles.newRolePlaceholder')}
        onChange={(e) => onChange(e.target.value.toLowerCase().replace(/\s+/g, '-'))}
        onBlur={() => {
          if (!value.trim()) {
            onChange('user');
          }
          setTyping(false);
        }}
        className={
          className ??
          'w-36 rounded border border-border bg-background px-1.5 py-0.5 text-sm outline-none focus:ring-2 focus:ring-ring'
        }
      />
    );
  }

  return (
    <select
      id={id}
      data-testid={testId}
      aria-label={label}
      value={value}
      onChange={(e) => {
        if (e.target.value === NEW_ROLE) {
          onChange('');
          setTyping(true);
        } else {
          onChange(e.target.value);
        }
      }}
      className={
        className ??
        'w-36 rounded border border-border bg-background px-1.5 py-0.5 text-sm outline-none focus:ring-2 focus:ring-ring'
      }
    >
      {roles.map((role) => (
        <option key={role} value={role}>
          {role}
        </option>
      ))}
      <option value={NEW_ROLE}>{t('startupData.roles.newRole')}</option>
    </select>
  );
}

export default RolePicker;
